import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../api/config/axiosConfig';
import { API_ENDPOINTS } from '../api/endpoints/endpoints';
import { formatPrice as formatCurrency } from '../utils/formatters';

interface Listing {
  id: string;
  slug: string;
  title: string;
  make: string;
  model: string;
  year: number;
  price: string | null;
  mileage: number | null;
  fuelType: string | null;
  city: string | null;
  promotionLevel: string;
  // findAll returns a flat image + thumbnailUrl, not a listingPhotos array
  image: string | null;
  thumbnailUrl: string | null;
}

const PLACEHOLDER_IMG = '/images/placeholder.jpg';

function formatPrice(price: string | null, priceOnRequest: string) {
  if (!price) return priceOnRequest;
  const n = Number(price);
  return isNaN(n) ? price : formatCurrency(n);
}

function formatMileage(km: number | null) {
  if (!km) return '—';
  return `${km.toLocaleString()} km`;
}

export default function FeaturedSection() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance
      .get(API_ENDPOINTS.LISTINGS.GET_ALL, {
        params: { sortBy: 'standard', limit: 20 },
      })
      .then((res) => {
        const data = res.data;
        setListings(data.listings ?? []);
      })
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="hp-section bg-white">
      <div className="hp-container">
        <div className="hp-section-header">
          <h2>{t('home.featured_title')}</h2>
          <p>{t('home.featured_subtitle')}</p>
        </div>

        {loading ? (
          <div className="row g-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="col-12 col-md-6 col-lg-3">
                <div className="hp-vehicle-card" style={{ minHeight: 300 }}>
                  <div
                    className="hp-vehicle-img"
                    style={{ background: '#f0f0f0', height: 180 }}
                  />
                  <div className="hp-vehicle-body">
                    <div style={{ background: '#f0f0f0', height: 20, borderRadius: 4, marginBottom: 8 }} />
                    <div style={{ background: '#f0f0f0', height: 16, borderRadius: 4, width: '60%' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <p className="text-center text-muted py-5">{t('home.featured_no_listings')}</p>
        ) : (
          <div className="row g-4">
            {listings.map((car) => {
              const img = car.thumbnailUrl || car.image || PLACEHOLDER_IMG;
              const title = car.title || `${car.year} ${car.make} ${car.model}`;
              const isBoosted =
                (car.promotionLevel === 'WEEK' || car.promotionLevel === 'MONTH') ;

              return (
                <div key={car.id} className="col-12 col-md-6 col-lg-3">
                  <div className="hp-vehicle-card" style={{ position: 'relative' }}>
                    {isBoosted && (
                      <span
                        style={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                          background: '#f59e0b',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 4,
                          zIndex: 1,
                        }}
                      >
                        {t('home.boosted')}
                      </span>
                    )}
                    <img
                      src={img}
                      alt={title}
                      className="hp-vehicle-img"
                      loading="lazy"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/listing/${car.slug || car.id}`)}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = PLACEHOLDER_IMG;
                      }}
                    />
                    <div className="hp-vehicle-body">
                      <h5 className="hp-vehicle-title">{title}</h5>
                      <div className="hp-vehicle-price">{formatPrice(car.price, t('home.price_on_request'))}</div>
                      <div className="hp-vehicle-meta">
                        {car.year && <span>{car.year}</span>}
                        {car.mileage != null && (
                          <>
                            <span>•</span>
                            <span>{formatMileage(car.mileage)}</span>
                          </>
                        )}
                        {car.fuelType && (
                          <>
                            <span>•</span>
                            <span>{car.fuelType}</span>
                          </>
                        )}
                      </div>
                      <button
                        className="hp-vehicle-btn"
                        onClick={() => navigate(`/listing/${car.slug || car.id}`)}
                      >
                        {t('home.view_details')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-5">
          <button className="btn-hp-outline" onClick={() => navigate('/mysearch')}>
            {t('home.view_all')}
          </button>
        </div>
      </div>
    </section>
  );
}
